import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";

interface PageBreadcrumbContextValue {
  /** A page-supplied trailing crumb (e.g. the entity name on a detail page).
   *  When set, the topbar appends it to the pathname-derived breadcrumb. */
  trailing: string | null;
  setTrailing: (next: string | null) => void;
  /** The name of the record the page shows, whether or not it appears in the breadcrumb. */
  subject: string | null;
  setSubject: (next: string | null) => void;
}

const PageBreadcrumbContext = createContext<PageBreadcrumbContextValue>({
  trailing: null,
  setTrailing: () => {},
  subject: null,
  setSubject: () => {},
});

export function PageBreadcrumbProvider({ children }: { children: ReactNode }) {
  const [trailing, setTrailing] = useState<string | null>(null);
  const [subject, setSubject] = useState<string | null>(null);
  const value = useMemo<PageBreadcrumbContextValue>(
    () => ({ trailing, setTrailing, subject, setSubject }),
    [trailing, subject],
  );
  return <PageBreadcrumbContext.Provider value={value}>{children}</PageBreadcrumbContext.Provider>;
}

export function usePageBreadcrumbState(): PageBreadcrumbContextValue {
  return useContext(PageBreadcrumbContext);
}

function useRegisteredName(
  name: string | null | undefined,
  set: (next: string | null) => void,
): void {
  useEffect(() => {
    set(name && name.length > 0 ? name : null);
    return () => set(null);
  }, [name, set]);
}

/// Call from a route to register a trailing breadcrumb (e.g. the title of a
/// detail entity). Clears automatically on unmount or when `title` becomes
/// nullish. Also names the page's record for Copilot, so a route that calls
/// this needs no `usePageSubject`.
export function usePageBreadcrumb(title: string | null | undefined): void {
  const { setTrailing, setSubject } = useContext(PageBreadcrumbContext);
  useRegisteredName(title, setTrailing);
  useRegisteredName(title, setSubject);
}

/// Names the record a page shows without adding a breadcrumb, for routes whose
/// breadcrumb is not changing but whose record Copilot should be able to name.
export function usePageSubject(name: string | null | undefined): void {
  useRegisteredName(name, useContext(PageBreadcrumbContext).setSubject);
}
